/**
 * Thin client for the ai-service. Same auth pattern as the other API
 * clients in the builder: ensures a fresh JWT then attaches it as a
 * Bearer header.
 */

import { ensureFreshToken } from '../auth/tokenRefresh';

const AI_ENDPOINT = (import.meta as any).env?.VITE_AI_ENDPOINT ?? 'http://localhost:3020';

/* ---------------------------------------------------------------------------
 * Model SWAP — types + endpoints
 *
 * The ai-service exposes per-flow allowlists and a per-request model
 * override. The chat UI uses these to surface a picker (Opus / Sonnet)
 * so users can pick the cost / quality trade-off per generation or per
 * chat session.
 * ------------------------------------------------------------------------- */

export interface ModelInfo {
  slug: string;
  /** Per-1M token rates (USD). Null when ai-service has no pricing entry. */
  inputPerM: number | null;
  outputPerM: number | null;
  /** False when pricing was guessed from a family name rather than known. */
  pricingKnown: boolean;
}

export interface FlowMeta {
  default: string;
  allowed: ModelInfo[];
}

export interface ModelsResponse {
  generate: FlowMeta;
  chat: FlowMeta;
}

/**
 * Fetch the per-flow model allowlist + rates. Cached client-side via
 * React Query (see useModels) — server already sends Cache-Control:
 * private, max-age=60, so cross-tab + reload also benefit.
 */
export async function fetchModels(): Promise<ModelsResponse> {
  const token = await ensureFreshToken();
  const res = await fetch(`${AI_ENDPOINT}/ai/models`, {
    method: 'GET',
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    throw new Error(`fetch models failed: ${res.status}`);
  }
  return res.json();
}

export interface PreviewNode {
  key: string;
  type: string;
  name: string;
  children: PreviewNode[];
}

export interface SalvageDrop {
  category: string;
  reason: string;
  path?: string;
  detail?: string;
}

export interface CostBreakdown {
  model: string;
  pricingKnown: boolean;
  totalUsd: number;
  inputCostUsd: number;
  outputCostUsd: number;
  cacheReadCostUsd: number;
  cacheWriteCostUsd: number;
  cacheSavingsUsd: number;
  noCacheTotalUsd: number;
}

export interface GenerateAppResponse {
  planId: string;
  preview: PreviewNode | null;
  usage?: {
    input_tokens: number;
    output_tokens: number;
    cache_read_input_tokens?: number;
    cache_creation_input_tokens?: number;
  };
  attempts: number;
  /** True when the server salvaged a partial plan in lenient mode. */
  salvaged?: boolean;
  /** Items pruned during salvage. */
  dropped?: SalvageDrop[];
  message?: string;
  /** Resolved model + whether the request overrode the server default. */
  model?: string;
  modelSource?: 'default' | 'override';
  /** USD cost breakdown the server computed for this generation. */
  cost?: CostBreakdown;
}

export interface GenerateAppFailedResponse {
  ok: false;
  errors: Array<{ category: string; message: string; path?: string }>;
  lastPlan: unknown;
  message: string;
  /** Even on hard failure, lenient mode may include drops it tried. */
  salvaged?: boolean;
  dropped?: SalvageDrop[];
}

export interface ApplyResponse {
  ok: true;
  appId: string;
  themeId: string;
  mainViewId: string;
  replacedRootId?: string | null;
}

export async function generateApp(args: {
  prompt: string;
  stylePreset?: string;
  appId?: string;
  /**
   * Optional model SWAP. When omitted, server uses its env default
   * (typically Opus 4.7). Must be one of the slugs in the `generate`
   * allowlist returned by /ai/models.
   */
  model?: string;
}): Promise<GenerateAppResponse | GenerateAppFailedResponse> {
  const token = await ensureFreshToken();
  const res = await fetch(`${AI_ENDPOINT}/ai/generate-app`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(args),
  });
  const json = await res.json();
  if (!res.ok && res.status !== 422) {
    throw new Error(`generate-app failed: ${res.status} ${json?.message ?? ''}`);
  }
  return json;
}

export async function applyPlan(args: {
  planId: string;
  appId?: string;
}): Promise<ApplyResponse> {
  const token = await ensureFreshToken();
  const res = await fetch(`${AI_ENDPOINT}/ai/generate-app/apply`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(args),
  });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(`apply failed: ${res.status} ${json?.message ?? ''}`);
  }
  return json;
}
